<template>
  <div :id="id" class="paypal-button" />
</template>

<script>
// Migrated from the deprecated `paypal-checkout` (Checkout.js v4) package to the
// modern `@paypal/paypal-js` JS SDK. The v4 SDK loaded its runtime bundle from
// paypalobjects.com/api/checkout.4.0.344.js, which PayPal has sunset (CORS /
// SDK_INTEGRATION_LOADING_ERROR). The modern SDK loads from paypal.com/sdk/js.
//
// IMPORTANT — backend contract: POST /api/transaction (and connector.service
// postTransaction) read the legacy v1 Payments response shape
// (transactions[].related_resources[].sale.id, payer.payer_info.*,
// item_list.items[]) WITHOUT null guards. The modern SDK returns a v2 Orders
// capture response with a different shape, so `toLegacyPaymentResponse()` maps
// v2 -> v1 before emitting `payment-completed`. This keeps the backend and the
// emitted prop/event interface unchanged.
import { defineComponent } from 'vue';
import { loadScript } from '@paypal/paypal-js';
import defaultProps from '../util/paypal/defaultProps..js';
import additionalProps from '../util/paypal/additionalProps.js';
import { DataLayerMixin } from '../mixins/dataLayerMixin';
import { useUserStore } from '@/stores/user';
import { toIsoDateStr } from '@/utils/dateUtils';

export default defineComponent({
  name: 'PayPalCheckout',
  props: Object.assign(defaultProps(), additionalProps.vmProps()),
  mixins: [DataLayerMixin],

  computed: {
    // Modern SDK takes a single client-id string. Resolve it from the legacy
    // { sandbox, production } client object + env prop (kept for compatibility).
    resolvedClientId() {
      if (typeof this.client === 'string') {
        return this.client;
      }
      return this.env === 'production' ? this.client?.production : this.client?.sandbox;
    },

    // The modern SDK `style` API supports only a subset of keys; the parent still
    // passes the legacy shape ({ label, size, shape, color }). Whitelist to the
    // supported keys so unsupported ones like `size` don't warn/reject on render.
    resolvedButtonStyle() {
      const { label, shape, color, height } = this.buttonStyle || {};
      const style = {};
      if (label) style.label = label;
      if (shape) style.shape = shape;
      if (color) style.color = color;
      if (height) style.height = height;
      return style;
    }
  },

  methods: {
    // Build the v2 Orders create payload. Kept intentionally minimal (single
    // amount + description + invoice) to avoid PayPal's strict
    // amount.breakdown / item_total validation. The per-item list that
    // Salesforce records is reconstructed from the `items` prop in the adapter,
    // exactly as the legacy flow produced it.
    createOrder(data, actions) {
      const purchaseUnit = {
        amount: {
          value: String(this.amount),
          currency_code: this.currency
        }
      };

      if (this.invoiceNumber) {
        purchaseUnit.invoice_id = this.invoiceNumber;
      }

      const description = this.items?.[0]?.description || this.courseTitle;
      if (description) {
        purchaseUnit.description = description;
      }

      return actions.order.create({
        intent: 'CAPTURE',
        purchase_units: [purchaseUnit],
        application_context: {
          brand_name: 'Arizona State University',
          shipping_preference: 'NO_SHIPPING',
          user_action: 'PAY_NOW'
        }
      });
    },

    async onApprove(data, actions) {
      const vue = this;
      vue.$emit('payment-authorized', data);

      let order;
      try {
        order = await actions.order.capture();
      } catch (err) {
        // INSTRUMENT_DECLINED is recoverable — let the buyer pick another funding
        // source instead of failing the checkout (PayPal's documented guidance).
        if (err?.message?.includes('INSTRUMENT_DECLINED')) {
          return actions.restart();
        }
        return this.onError(err);
      }

      // A resolved capture is NOT necessarily a completed payment — it can come back
      // PENDING/DECLINED. Only a COMPLETED capture may be recorded as an enrollment +
      // Salesforce ledger row; otherwise we'd book an unpaid order.
      const capture = order?.purchase_units?.[0]?.payments?.captures?.[0];
      if (capture?.status !== 'COMPLETED') {
        return this.onError(new Error(`CAPTURE_NOT_COMPLETED:${capture?.status || 'UNKNOWN'}`));
      }

      const response = this.toLegacyPaymentResponse(order);

      // Clear previous ecommerce object
      if (window.dataLayer) {
        window.dataLayer.push({ ecommerce: null });
      }

      // Format course title to lowercase with underscores
      const formattedCourseTitle = (this.courseTitle || '').toLowerCase().replace(/\s+/g, '_');
      const userStore = useUserStore();
      const leSfContactId = userStore.apis?.learnerData?._contactId || userStore.apis?.learnerData?.contactId || '';

      // Add purchase event for successful payment
      const purchaseEvent = {
        event: 'purchase',
        le_sf_contact_id: leSfContactId,
        ecommerce: {
          transaction_id: response.id || data.orderID,
          value: parseFloat(this.amount),
          currency: 'USD',
          items: [
            {
              item_id: formattedCourseTitle,
              item_name: formattedCourseTitle,
              course_offering_id: this.offeringId || '',
              course_name: this.courseName || '',
              course_prefix: this.coursePrefix || '',
              course_start_date: toIsoDateStr(this.courseStartDate),
              course_end_date: toIsoDateStr(this.courseEndDate),
              price: parseFloat(this.amount),
              quantity: 1
            }
          ]
        }
      };

      this.asuoDataLayerClick('ecommerceTags', '0', purchaseEvent);

      vue.$emit('payment-completed', response);
      return response;
    },

    // Adapter: PayPal v2 Orders capture response -> legacy v1 Payments response.
    // Every path the backend dereferences without a guard is always present:
    // transactions[].amount.details, .item_list.items[], .related_resources[].sale,
    // payer.payer_info.*
    toLegacyPaymentResponse(order = {}) {
      const purchaseUnit = order.purchase_units?.[0] || {};
      const capture = purchaseUnit.payments?.captures?.[0] || {};
      const payerName = order.payer?.name || {};

      return {
        id: order.id,
        payer: {
          payment_method: 'paypal',
          payer_info: {
            email: order.payer?.email_address,
            first_name: payerName.given_name,
            last_name: payerName.surname,
            payer_id: order.payer?.payer_id
          }
        },
        transactions: [
          {
            amount: {
              total: purchaseUnit.amount?.value ?? String(this.amount),
              currency: purchaseUnit.amount?.currency_code ?? this.currency,
              // Backend reads amount.details.* — must be an object (matches legacy `details` prop, default {}).
              details: this.details || {}
            },
            description: purchaseUnit.description,
            invoice_number: purchaseUnit.invoice_id ?? this.invoiceNumber,
            payee: {
              email: purchaseUnit.payee?.email_address,
              merchant_id: purchaseUnit.payee?.merchant_id
            },
            // Reconstructed from the `items` prop, the same source the legacy flow used.
            item_list: {
              items: (this.items || []).map((item) => ({
                name: item.name,
                description: item.description,
                quantity: item.quantity,
                price: item.price,
                currency: item.currency || this.currency,
                sku: item.sku,
                tax: item.tax
              }))
            },
            related_resources: [
              {
                sale: {
                  id: capture.id,
                  parent_payment: order.id,
                  state: capture.status
                }
              }
            ]
          }
        ]
      };
    },

    onCancel(data) {
      this.$emit('payment-cancelled', data);
    },

    onError(err) {
      // SDK load / order / capture failures are errors, not user cancellations.
      // Emit a distinct event so the parent surfaces its payment-error dialog
      // (with a contact-support path) rather than silently resetting — otherwise a
      // failed SDK load looks identical to the outage this migration fixes.
      this.$emit('payment-error', { error: 'PAYPAL_ERROR', detail: err?.message || String(err) });
    },

    onClick() {
      this.asuoDataLayerClick('courseCheckout', '2', { component: this.courseTitle || '' });

      // Clear previous ecommerce object
      if (window.dataLayer) {
        window.dataLayer.push({ ecommerce: null });
      }

      // Format course title to lowercase with underscores
      const formattedCourseTitle = (this.courseTitle || '').toLowerCase().replace(/\s+/g, '_');
      const userStore = useUserStore();
      const leSfContactId = userStore.apis?.learnerData?._contactId || userStore.apis?.learnerData?.contactId || '';

      //ecom data layer
      const ecom = {
        event: 'begin_checkout',
        le_sf_contact_id: leSfContactId,
        ecommerce: {
          currency: 'USD',
          value: parseFloat(this.amount),
          items: [
            {
              item_id: formattedCourseTitle,
              item_name: formattedCourseTitle,
              course_offering_id: this.offeringId || '',
              course_name: this.courseName || '',
              course_prefix: this.coursePrefix || '',
              course_start_date: toIsoDateStr(this.courseStartDate),
              course_end_date: toIsoDateStr(this.courseEndDate),
              price: parseFloat(this.amount),
              quantity: 1
            }
          ]
        }
      };

      this.asuoDataLayerClick('ecommerceTags', '0', ecom);
    }
  },

  async mounted() {
    // Surface env/config mistakes loudly instead of rendering a dead button.
    if (!this.resolvedClientId) {
      console.error('[PaypalCheckout] Missing PayPal client id for env:', this.env);
      this.onError(new Error('MISSING_PAYPAL_CLIENT_ID'));
      return;
    }

    try {
      const paypal = await loadScript({
        clientId: this.resolvedClientId,
        currency: this.currency,
        intent: 'capture',
        components: 'buttons'
      });

      if (!paypal?.Buttons) {
        this.onError(new Error('SDK_LOAD_FAILED'));
        return;
      }

      this.buttons = paypal.Buttons({
        style: this.resolvedButtonStyle,
        createOrder: this.createOrder,
        onApprove: this.onApprove,
        onCancel: this.onCancel,
        onClick: this.onClick,
        onError: this.onError
      });

      if (this.buttons.isEligible && !this.buttons.isEligible()) {
        return;
      }

      this.buttons.render(this.$el);
    } catch (err) {
      this.onError(err);
    }
  },

  beforeUnmount() {
    // Parent toggles this component via v-if (voucher/amount changes); close the
    // rendered button on unmount to avoid orphaned instances/listeners.
    this.buttons?.close?.();
  }
});
</script>
